import { Box, Flex, Image, Spinner } from "@chakra-ui/react"
import { Outlet, createFileRoute, redirect } from "@tanstack/react-router"

import { useColorModeValue } from "@/components/ui/color-mode"
import { OrganizationProvider } from "@/contexts/OrganizationContext"
import { useSidebar } from "@/contexts/SidebarContext"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import {
  fetchDemoBanner,
  isDemoEnabledSync,
  mintVisitorSession,
} from "@/lib/demoAuth"
import LogoLight from "/qdat-logo-light.svg"
import Logo from "/qdat-logo.svg"
import { SidebarDesktop, SidebarMobile } from "../components/Common/Sidebar"
import { TwoSvNudge } from "../components/Common/TwoSvNudge"
import useAuth, { isLoggedIn } from "../hooks/useAuth"

export const Route = createFileRoute("/_layout")({
  component: Layout,
  beforeLoad: async () => {
    if (isLoggedIn()) return

    // Not logged in. If we have a recent mirror saying "demo on",
    // mint a visitor session and continue. Otherwise check the
    // banner endpoint once — covers the cold-load case where the
    // mirror hasn't been set yet (e.g. brand new tab).
    if (isDemoEnabledSync()) {
      try {
        await mintVisitorSession()
        return
      } catch {
        // fall through to login if the mint fails
      }
    } else {
      try {
        const banner = await fetchDemoBanner()
        if (banner.enabled) {
          await mintVisitorSession()
          return
        }
      } catch {
        // ignore — fall through to login
      }
    }

    throw redirect({
      to: "/login",
    })
  },
})

function Layout() {
  const { isLoading } = useAuth()
  const sc = useSemanticColors()
  const { sidebarWidth } = useSidebar()
  const currentLogo = useColorModeValue(LogoLight, Logo)
  const mobileBarBg = useColorModeValue(
    "rgba(255, 255, 255, 0.94)",
    "rgba(15, 21, 44, 0.94)",
  )
  const mobileBarBorder = useColorModeValue(
    "#E2E8F0",
    "rgba(255, 255, 255, 0.1)",
  )

  return (
    <OrganizationProvider>
      <SidebarDesktop />

      {/* Mobile top bar with hamburger — visible only on base–sm */}
      <Flex
        display={{ base: "flex", md: "none" }}
        position="sticky"
        top={0}
        zIndex={50}
        align="center"
        h="56px"
        px={2}
        bg={mobileBarBg}
        backdropFilter="blur(20px)"
        borderBottom="1px solid"
        borderColor={mobileBarBorder}
      >
        <SidebarMobile />
        <Flex flex={1} justify="center" pr="48px">
          <Image src={currentLogo} alt="QDat logo" h="28px" />
        </Flex>
      </Flex>

      <Box
        ml={{ base: 0, md: `${sidebarWidth}px` }}
        w={{ base: "100%", md: `calc(100% - ${sidebarWidth}px)` }}
        maxW="100vw"
        transition="all 0.2s linear"
        minH="100vh"
        overflowX="hidden"
      >
        <Box
          mx="auto"
          maxW="100%"
          p={{
            base: "12px 8px",
            sm: "16px 12px",
            md: "20px 20px",
            lg: "28px 28px",
          }}
        >
          <TwoSvNudge />
          <Box minH="calc(100vh - 123px)">
            {isLoading ? (
              <Flex justify="center" align="center" height="60vh" width="full">
                <Spinner size="xl" color={sc.spinnerColor} />
              </Flex>
            ) : (
              <Outlet />
            )}
          </Box>
        </Box>
      </Box>
    </OrganizationProvider>
  )
}
